import { Navigate, useLocation, useParams } from 'react-router-dom';
import { PageLoader } from '../components/ui';
import { ALL, OWNER, STAFF } from '../constants/permissions';
import ActivityList from '../modules/activity/pages/ActivityList';
import HelpGuide from '../modules/help/pages/HelpGuide';
import WebsiteSettings from '../modules/website/pages/WebsiteSettings';
import Attendance from '../modules/attendance/pages/Attendance';
import ChangePassword from '../modules/auth/pages/ChangePassword';
import CustomerDetails from '../modules/customers/pages/CustomerDetails';
import CustomerForm from '../modules/customers/pages/CustomerForm';
import CustomerList from '../modules/customers/pages/CustomerList';
import ExpenseDetails from '../modules/expenses/pages/ExpenseDetails';
import ExpenseForm from '../modules/expenses/pages/ExpenseForm';
import ExpenseList from '../modules/expenses/pages/ExpenseList';
import InvoiceDetails from '../modules/invoices/pages/InvoiceDetails';
import InvoiceForm from '../modules/invoices/pages/InvoiceForm';
import InvoiceList from '../modules/invoices/pages/InvoiceList';
import JobBoard from '../modules/jobs/pages/JobBoard';
import JobDetails from '../modules/jobs/pages/JobDetails';
import JobForm from '../modules/jobs/pages/JobForm';
import JobList from '../modules/jobs/pages/JobList';
import MasterForm from '../modules/masters/pages/MasterForm';
import MasterList from '../modules/masters/pages/MasterList';
import MaterialDetails from '../modules/materials/pages/MaterialDetails';
import MaterialForm from '../modules/materials/pages/MaterialForm';
import MaterialList from '../modules/materials/pages/MaterialList';
import PayslipDetails from '../modules/payroll/pages/PayslipDetails';
import PayslipForm from '../modules/payroll/pages/PayslipForm';
import PayslipList from '../modules/payroll/pages/PayslipList';
import StaffAdvanceList from '../modules/payroll/pages/StaffAdvanceList';
import PurchaseDetails from '../modules/purchases/pages/PurchaseDetails';
import PurchaseEdit from '../modules/purchases/pages/PurchaseEdit';
import PurchaseForm from '../modules/purchases/pages/PurchaseForm';
import PurchaseList from '../modules/purchases/pages/PurchaseList';
import QuotationConvert from '../modules/quotations/pages/QuotationConvert';
import QuotationDetails from '../modules/quotations/pages/QuotationDetails';
import QuotationForm from '../modules/quotations/pages/QuotationForm';
import QuotationList from '../modules/quotations/pages/QuotationList';
import Outstanding from '../modules/reports/pages/Outstanding';
import Reports from '../modules/reports/pages/Reports';
import Settings from '../modules/settings/pages/Settings';
import StockAdjust from '../modules/stock/pages/StockAdjust';
import StockList from '../modules/stock/pages/StockList';
import TeamForm from '../modules/team/pages/TeamForm';
import TeamList from '../modules/team/pages/TeamList';
import TemplateForm from '../modules/templates/pages/TemplateForm';
import TemplateList from '../modules/templates/pages/TemplateList';
import TransactionDetails from '../modules/transactions/pages/TransactionDetails';
import TransactionForm from '../modules/transactions/pages/TransactionForm';
import TransactionList from '../modules/transactions/pages/TransactionList';
import VendorDetails from '../modules/vendors/pages/VendorDetails';
import VendorForm from '../modules/vendors/pages/VendorForm';
import VendorList from '../modules/vendors/pages/VendorList';
import WorkRateForm from '../modules/work-rates/pages/WorkRateForm';
import WorkRateList from '../modules/work-rates/pages/WorkRateList';
import { useAuth } from './providers/AuthProvider';
import type { ComponentType, ReactElement } from 'react';
import type { Role } from '../constants/roles';

export function RequireAuth({ children }: { children: ReactElement }) {
  const { user, booting } = useAuth();
  const location = useLocation();
  if (booting) return <PageLoader label="Opening Electroz" />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  return children;
}

export function RequireRole({ roles, children }: { roles: Role[]; children: ReactElement }) {
  const { user } = useAuth();
  return user && roles.includes(user.role) ? children : <Navigate to="/" replace />;
}

/** Old "Products" links: /items -> /materials (the material page forwards work items to Work rates). */
export function ItemsRedirect() {
  const { id } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/materials${id ? `/${id}` : ''}${search}`} replace />;
}

export interface PageRoute {
  path: string;
  page: ComponentType;
  roles: Role[];
}

const r = (path: string, page: ComponentType, roles: Role[]): PageRoute => ({ path, page, roles });

// Create and edit screens are full pages (…/new, …/:id/edit), never modals.
export const PAGES: PageRoute[] = [
  r('customers', CustomerList, STAFF),
  r('customers/new', CustomerForm, STAFF),
  r('customers/:id', CustomerDetails, STAFF),
  r('customers/:id/edit', CustomerForm, STAFF),

  r('quotations', QuotationList, STAFF),
  r('quotations/new', QuotationForm, STAFF),
  r('quotations/:id', QuotationDetails, STAFF),
  r('quotations/:id/edit', QuotationForm, STAFF),
  r('quotations/:id/convert', QuotationConvert, STAFF),

  r('jobs', JobList, ALL),
  r('jobs/board', JobBoard, ALL),
  r('jobs/new', JobForm, STAFF),
  r('jobs/:id', JobDetails, ALL),
  r('jobs/:id/edit', JobForm, STAFF),

  r('invoices', InvoiceList, STAFF),
  r('invoices/new', InvoiceForm, STAFF),
  r('invoices/:id', InvoiceDetails, STAFF),
  r('invoices/:id/edit', InvoiceForm, STAFF),

  r('materials', MaterialList, STAFF),
  r('materials/new', MaterialForm, STAFF),
  r('materials/:id', MaterialDetails, STAFF),
  r('materials/:id/edit', MaterialForm, STAFF),

  r('work-rates', WorkRateList, STAFF),
  r('work-rates/new', WorkRateForm, STAFF),
  r('work-rates/:id/edit', WorkRateForm, STAFF),

  r('stock', StockList, STAFF),
  r('stock/adjust', StockAdjust, STAFF),

  r('purchases', PurchaseList, STAFF),
  r('purchases/new', PurchaseForm, STAFF),
  r('purchases/:id', PurchaseDetails, STAFF),
  r('purchases/:id/edit', PurchaseEdit, STAFF),

  r('vendors', VendorList, STAFF),
  r('vendors/new', VendorForm, STAFF),
  r('vendors/:id', VendorDetails, STAFF),
  r('vendors/:id/edit', VendorForm, STAFF),

  r('transactions', TransactionList, STAFF),
  r('transactions/new', TransactionForm, STAFF),
  r('transactions/:id', TransactionDetails, STAFF),

  r('expenses', ExpenseList, STAFF),
  r('expenses/new', ExpenseForm, STAFF),
  r('expenses/:id', ExpenseDetails, STAFF),
  r('expenses/:id/edit', ExpenseForm, STAFF),

  r('outstanding', Outstanding, STAFF),
  r('reports', Reports, STAFF),

  r('masters', MasterList, STAFF),
  r('masters/:type', MasterList, STAFF),
  r('masters/:type/new', MasterForm, STAFF),
  r('masters/:type/:id/edit', MasterForm, STAFF),

  r('templates', TemplateList, STAFF),
  r('templates/new', TemplateForm, STAFF),
  r('templates/:id/edit', TemplateForm, STAFF),

  r('attendance', Attendance, STAFF),
  r('payroll', PayslipList, OWNER),
  r('payroll/new', PayslipForm, OWNER),
  r('payroll/:id', PayslipDetails, OWNER),
  r('staff-advances', StaffAdvanceList, OWNER),

  r('team', TeamList, OWNER),
  r('team/new', TeamForm, OWNER),
  r('team/:id/edit', TeamForm, OWNER),

  r('activity', ActivityList, OWNER),
  r('help', HelpGuide, ALL),
  r('settings', Settings, OWNER),
  r('website', WebsiteSettings, OWNER),
  r('account/password', ChangePassword, ALL),
];
